import { t } from "@bezbolisno/i18n";
import { can } from "@bezbolisno/kit/rbac";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { PageHeader } from "../../../components/page-header";
import { api, expectOk } from "../../../lib/api";
import { keys } from "../../../lib/keys";
import { ownSpecialistId } from "../../../lib/own-specialist";
import { BookingForm } from "../../../screens/schedule/booking-form";
import { BookingSearch, bookingContextQuery } from "../../../screens/schedule/queries";

const patientQuery = (patientId: string) =>
  queryOptions({
    queryKey: keys.patient(patientId),
    queryFn: async () => expectOk(await api.patients.get({ params: { patientId } }), 200),
  });

/** Booking page (replaces the schedule modal — the calendar gets real room). */
export const Route = createFileRoute("/_authed/schedule/new")({
  validateSearch: BookingSearch,
  beforeLoad: ({ context }) => {
    // requirePageAccess("appointment:manage") — the API enforces it too (403).
    if (!can(context.session.role, "appointment:manage")) throw redirect({ to: "/" });
  },
  loaderDeps: ({ search }) => ({ specialist: search.specialist, date: search.date }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(
      bookingContextQuery(ownSpecialistId(context.session) ?? deps.specialist, deps.date),
    ),
  component: NewAppointmentPage,
});

function NewAppointmentPage() {
  const { specialist, date, patient } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const locked = ownSpecialistId(session);
  const { data: ctx } = useSuspenseQuery(bookingContextQuery(locked ?? specialist, date));
  const preselected = useQuery({ ...patientQuery(patient ?? ""), enabled: Boolean(patient) });

  // A specialist books onto their own schedule, no picker; for others the API's
  // `defaultSpecialistId` preselects the week filter, else «Спеціаліст пацієнта».
  return (
    <div className="fade-in">
      <PageHeader title={t.appointments.book} />
      <BookingForm
        key={preselected.data?.id ?? "none"}
        specialists={ctx.specialists}
        lockedSpecialistId={locked}
        defaultSpecialistId={ctx.defaultSpecialistId ?? ""}
        defaultDate={ctx.defaultDate}
        preselected={
          preselected.data
            ? {
                id: preselected.data.id,
                name: preselected.data.name,
                status: preselected.data.status,
                sport: preselected.data.sport,
              }
            : null
        }
      />
    </div>
  );
}
